import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import SocialPostsAPI from 'dashboard/api/socialPosts';
import { createStore } from 'dashboard/store/storeFactory';
import { throwErrorMessage } from 'dashboard/store/utils/api';

const createInitialUIFlags = () => ({
  fetchingList: false,
  fetchingScheduledList: false,
  creatingItem: false,
  updatingItem: false,
  deletingItem: false,
  generatingContent: false,
  generatingImagePrompt: false,
});

const camelizePost = data => camelcaseKeys(data || {}, { deep: true });

const normalizeMeta = meta => ({
  ...camelcaseKeys(meta || {}),
  totalCount: Number(meta?.total_count || meta?.totalCount || 0),
  page: Number(meta?.current_page || meta?.page || 1),
});

// SocialPost has no status/scheduled column -- `posted_at` doubles as the
// scheduled time for a scheduled post (set once by
// Social::Facebook::PostPublisher, either `scheduled_at` or `Time.current`)
// and as the actual publish time for a published one. So "is this post
// scheduled" is a plain comparison against now, mirroring exactly what the
// backend's `index` action does when splitting on the `scheduled` param.
const isScheduledPost = post =>
  Boolean(post.postedAt) && new Date(post.postedAt) > new Date();

// Both lists come back from the server `recent_first` (posted_at desc). A newly created post
// appended to the end would sit below every older one until the next fetch reordered it -- a
// post published seconds ago showing up at the bottom of Published reads as a failure.
const recentFirst = posts =>
  [...posts].sort(
    (a, b) => new Date(b.postedAt || 0) - new Date(a.postedAt || 0)
  );

// `snakecaseKeys` with `deep: true` recurses into every plain-object-shaped
// value it finds -- a File instance has no own enumerable properties, so it
// gets flattened into `{}` and the upload is silently lost. Only the
// non-file fields go through it; when an image is present the request has
// to become multipart/form-data instead, the same way `companies.js`
// switches to FormData for an avatar upload.
const buildCreatePayload = ({ image, video, ...rest }) => {
  const payload = snakecaseKeys(rest, { deep: true });
  // snakecaseKeys flattens a File to {} -- it has no own enumerable properties -- so any
  // upload has to be appended to FormData by hand, after the rest has been converted.
  const upload = image || video;
  if (!upload) return payload;

  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    formData.append(key, value);
  });
  formData.append(image ? 'image' : 'video', upload);
  return formData;
};

export const useSocialPostsStore = createStore({
  name: 'socialPosts',
  type: 'pinia',
  API: SocialPostsAPI,
  state: () => ({
    uiFlags: createInitialUIFlags(),
    // Published (records/meta, from the base factory state) and scheduled
    // (scheduledRecords/scheduledMeta) are fetched independently -- the Posts
    // tab shows both lists at once, so they cannot share one slice the way
    // socialComments' single queue does.
    scheduledRecords: [],
    scheduledMeta: {},
  }),

  actions: () => ({
    setMeta(meta) {
      this.meta = normalizeMeta(meta);
    },

    setScheduledMeta(meta) {
      this.scheduledMeta = normalizeMeta(meta);
    },

    upsertRecord(post) {
      const target = isScheduledPost(post) ? 'scheduledRecords' : 'records';
      const index = this[target].findIndex(r => r.id === post.id);
      if (index === -1) this[target] = recentFirst([...this[target], post]);
      else this[target][index] = post;
    },

    async get({ inboxId, scheduled } = {}) {
      const flag = scheduled ? 'fetchingScheduledList' : 'fetchingList';
      this.setUIFlag({ [flag]: true });
      try {
        const {
          data: { payload, meta },
        } = await SocialPostsAPI.get({ inboxId, scheduled });
        const posts = camelizePost(payload);
        if (scheduled) {
          this.scheduledRecords = posts;
          this.setScheduledMeta(meta);
        } else {
          this.records = posts;
          this.setMeta(meta);
        }
        return posts;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ [flag]: false });
      }
    },

    // The API sends the request body straight through to Rails strong params
    // (`post_params` permits :inbox_id, :message, :link, :image,
    // :scheduled_at), so a camelCase payload from the composer has to be
    // snake_cased here -- unlike `reply`/`hide`/etc. on socialComments,
    // which only ever send a flat `message` key that needs no conversion.
    async create(payload) {
      this.setUIFlag({ creatingItem: true });
      try {
        const { data } = await SocialPostsAPI.create(
          buildCreatePayload(payload)
        );
        const post = camelizePost(data);
        this.upsertRecord(post);
        return post;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    // Drafts post copy from a brief. Returns the content for the composer to
    // drop into its editable message field -- this never creates or touches
    // a SocialPost record, so there is no path from here to `create`/publish.
    // upsertRecord routes by schedule state on its own, and an edit cannot move a post
    // between the two lists: only a still-scheduled post may be rescheduled, and the new
    // time is validated to be in the future, so it stays scheduled.
    async update({ id, message, scheduledAt }) {
      this.setUIFlag({ updatingItem: true });
      try {
        const { data } = await SocialPostsAPI.update(id, {
          message,
          scheduledAt,
        });
        const post = camelizePost(data);
        this.upsertRecord(post);
        return post;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async generate({ brief }) {
      this.setUIFlag({ generatingContent: true });
      try {
        const {
          data: { content },
        } = await SocialPostsAPI.generate({ brief });
        return content;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ generatingContent: false });
      }
    },

    // Writes an image-generation prompt from a brief. Returns the prompt
    // text for the composer to display in its own read-only block -- this
    // never touches `message`, never creates or touches a SocialPost
    // record, and calls no image model.
    async generateImagePrompt({ brief }) {
      this.setUIFlag({ generatingImagePrompt: true });
      try {
        const {
          data: { prompt },
        } = await SocialPostsAPI.generateImagePrompt({ brief });
        return prompt;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ generatingImagePrompt: false });
      }
    },

    // Also serves as "cancel a scheduled post": DELETE /social_posts/:id is
    // the same endpoint for both, per the backend contract.
    async delete(id) {
      this.setUIFlag({ deletingItem: true });
      try {
        await SocialPostsAPI.delete(id);
        this.records = this.records.filter(r => r.id !== Number(id));
        this.scheduledRecords = this.scheduledRecords.filter(
          r => r.id !== Number(id)
        );
        return Number(id);
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },
  }),
});
